import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogIn, Menu, Moon, ShoppingBag, Sun, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { avatarUrl } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/quiz", label: "Skin Quiz" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { count, setDrawerOpen, theme, toggleTheme } = useStore();
  const { user, login } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const avatar = avatarUrl(user?.picture);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[100] transition-all duration-500 ${scrolled ? "glass py-2" : "bg-transparent py-4"}`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" aria-label="Mother Nature home">
          <Logo size={34} />
        </Link>

        <nav className="hidden items-center gap-8 md:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-300 hover:text-[var(--mn-gold)] ${
                  isActive ? "text-[var(--mn-gold)]" : "text-[var(--mn-text)]/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <button
              data-testid="nav-account-button"
              onClick={() => navigate("/account")}
              aria-label="My account"
              className="btn-outline-glow flex h-10 w-10 items-center justify-center overflow-hidden rounded-full"
            >
              {avatar ? (
                <img src={avatar} alt={user.name} className="h-full w-full object-cover" />
              ) : (
                <span className="font-serif text-sm" style={{ color: "var(--mn-gold)" }}>
                  {(user.name || "M")[0].toUpperCase()}
                </span>
              )}
            </button>
          ) : (
            <button
              data-testid="nav-signin-button"
              onClick={login}
              aria-label="Sign in with Google"
              className="btn-outline-glow hidden h-10 items-center gap-1.5 rounded-full px-4 text-xs font-medium sm:flex"
            >
              <LogIn size={14} style={{ color: "var(--mn-gold)" }} /> Sign In
            </button>
          )}
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle light and dark mode"
            className="btn-outline-glow flex h-10 w-10 items-center justify-center rounded-full"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button
            data-testid="cart-button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open cart"
            className="btn-outline-glow relative flex h-10 w-10 items-center justify-center rounded-full"
          >
            <ShoppingBag size={17} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 font-mono text-[0.6rem] font-bold"
                style={{ background: "var(--mn-gold)", color: "#070D09" }}
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
            className="btn-outline-glow flex h-10 w-10 items-center justify-center rounded-full md:hidden"
          >
            {menuOpen ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="glass mt-2 overflow-hidden md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => {
                    setMenuOpen(false);
                    navigate(l.to);
                  }}
                  className="rounded-lg px-3 py-3 text-left font-serif text-lg transition-colors hover:bg-[var(--mn-surface)] hover:text-[var(--mn-gold)]"
                >
                  {l.label}
                </button>
              ))}
              {user ? (
                <button
                  data-testid="mobile-nav-account"
                  onClick={() => { setMenuOpen(false); navigate("/account"); }}
                  className="rounded-lg px-3 py-3 text-left font-serif text-lg transition-colors hover:bg-[var(--mn-surface)] hover:text-[var(--mn-gold)]"
                >
                  My Account
                </button>
              ) : (
                <button
                  data-testid="mobile-nav-signin"
                  onClick={() => { setMenuOpen(false); login(); }}
                  className="rounded-lg px-3 py-3 text-left font-serif text-lg transition-colors hover:bg-[var(--mn-surface)] hover:text-[var(--mn-gold)]"
                >
                  Sign In with Google
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
